import { inputClass, secondaryBtnClass, secondarySurfaceClass } from "@/components/common/surfaceClasses";
import type { Dispatch, SetStateAction } from "react";
import { enqueueFeedbackEvent } from "../../../lib/training/feedbackQueue";
import { submitTrainingFeedbackQueue } from "../../../lib/training/feedbackSubmit";
import type { TrainingAnswer } from "../../../lib/training/types";
import type { KbLane } from "@/lib/kb/types";
import { adminPost } from "@/lib/kbAdminClient";
import { addTrainingThreadFeedback } from "@/lib/training/trainerThreadingClient";
import type { GuidanceSnapshot } from "./trainingPanelGuidance";
import { isThreadNotFoundError } from "./trainingPanelGuidance";

const FEEDBACK_TOP_HITS_LIMIT = 3;

type ThumbsDownReason =
  | ""
  | "wrong_answer"
  | "missing_detail"
  | "outdated_policy"
  | "slang_uncommon_phrasing"
  | "wrong_contacts";

type MessageTone = { tone: "ok" | "err"; text: string } | null;

type TrainingAskThumbsDownFormProps = {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  busy: boolean;
  setBusy: Dispatch<SetStateAction<boolean>>;
  reason: ThumbsDownReason;
  setReason: Dispatch<SetStateAction<ThumbsDownReason>>;
  notes: string;
  setNotes: Dispatch<SetStateAction<string>>;
  expectedAnswer: string;
  setExpectedAnswer: Dispatch<SetStateAction<string>>;
  impact: string;
  setImpact: Dispatch<SetStateAction<string>>;
  message: MessageTone;
  setMessage: Dispatch<SetStateAction<MessageTone>>;
  kbTestingModeEnabled: boolean;
  result: TrainingAnswer;
  appVersion: string;
  resultQuery: string;
  lastQuery: string;
  query: string;
  askThreadId: string | null;
  askThreadTurnsCount: number;
  nextAskKind: "new" | "followup";
  locationId?: string;
  locationName?: string;
  salespersonFirstName?: string;
  salespersonName?: string;
  scopedOrganizationId: string;
  canEscalateKbThread: boolean;
  activeGuidance: GuidanceSnapshot | null;
  webhookUrl?: string;
};

export function TrainingAskThumbsDownForm(props: TrainingAskThumbsDownFormProps) {
  if (!props.open) return null;

  const resetForm = () => {
    props.setOpen(false);
    props.setReason("");
    props.setNotes("");
    props.setExpectedAnswer("");
    props.setImpact("");
    props.setMessage(null);
  };

  return (
    <div className={`mt-3 ${secondarySurfaceClass} p-3 space-y-2`}>
      <div className="text-xs font-semibold text-gray-800 dark:text-zinc-200">
        {props.kbTestingModeEnabled ? "KB testing feedback form" : "What was wrong?"}
      </div>
      {props.kbTestingModeEnabled ? (
        <div className="text-[11px] text-gray-600">
          Fill all required fields. This submits directly into KB Gap Inbox tagged as KB testing.
        </div>
      ) : null}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <label className="text-xs inline-flex items-center gap-2"><input type="radio" name="thumbs-down-reason" checked={props.reason === "wrong_answer"} onChange={() => props.setReason("wrong_answer")} />Wrong answer</label>
        <label className="text-xs inline-flex items-center gap-2"><input type="radio" name="thumbs-down-reason" checked={props.reason === "missing_detail"} onChange={() => props.setReason("missing_detail")} />Missing detail</label>
        <label className="text-xs inline-flex items-center gap-2"><input type="radio" name="thumbs-down-reason" checked={props.reason === "outdated_policy"} onChange={() => props.setReason("outdated_policy")} />Outdated policy/info</label>
        <label className="text-xs inline-flex items-center gap-2"><input type="radio" name="thumbs-down-reason" checked={props.reason === "slang_uncommon_phrasing"} onChange={() => props.setReason("slang_uncommon_phrasing")} />Slang / uncommon phrasing</label>
        <label className="text-xs inline-flex items-center gap-2 sm:col-span-2"><input type="radio" name="thumbs-down-reason" checked={props.reason === "wrong_contacts"} onChange={() => props.setReason("wrong_contacts")} />Wrong contact recommendation</label>
      </div>
      <textarea className={`w-full ${inputClass} min-h-[88px]`} placeholder="What did you expect to see? Include exact wording if slang/local phrasing should map to a KB topic." value={props.notes} onChange={(e) => props.setNotes(e.target.value)} />
      {props.kbTestingModeEnabled ? (
        <>
          <label className="block text-xs text-gray-700 dark:text-zinc-300">Expected answer (required)<textarea className={`mt-1 w-full ${inputClass} min-h-[72px]`} value={props.expectedAnswer} onChange={(e) => props.setExpectedAnswer(e.target.value)} placeholder="What answer should the user have received?" /></label>
          <label className="block text-xs text-gray-700 dark:text-zinc-300">One-sentence impact (required)<input className={`mt-1 w-full ${inputClass}`} value={props.impact} onChange={(e) => props.setImpact(e.target.value)} placeholder="How this hurts real work if not fixed" /></label>
        </>
      ) : null}
      {props.message ? <div className={props.message.tone === "ok" ? "text-xs text-emerald-700" : "text-xs text-red-700"}>{props.message.text}</div> : null}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={props.busy || !props.reason || (props.kbTestingModeEnabled && (!props.expectedAnswer.trim() || !props.impact.trim()))}
          className="rounded-lg bg-gray-900 px-2.5 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
          onClick={async () => {
            props.setBusy(true);
            props.setMessage(null);
            const topHits = props.result.hits.slice(0, FEEDBACK_TOP_HITS_LIMIT);
            const topHitIds = topHits.map((h) => h.id);
            const topHitTitles = topHits.map((h) => h.title);
            const topHitScores = topHits.map((h) => h.score);
            const activeQuery = props.resultQuery || props.lastQuery || props.query.trim();
            const notes = props.notes.trim();
            const expectedAnswer = props.expectedAnswer.trim();
            const impact = props.impact.trim();
            const reasonLabel = props.reason === "wrong_answer" ? "Wrong answer" : props.reason === "missing_detail" ? "Missing detail" : props.reason === "outdated_policy" ? "Outdated policy/info" : props.reason === "slang_uncommon_phrasing" ? "Slang / uncommon phrasing" : "Wrong contact recommendation";
            const feedbackText = props.kbTestingModeEnabled ? [`Reason: ${reasonLabel}`, `Exact question phrasing: ${activeQuery}`, `Expected answer: ${expectedAnswer || "(not provided)"}`, `What was wrong: ${notes || "(not provided)"}`, `Impact: ${impact || "(not provided)"}`].join(" | ") : `${reasonLabel}${notes ? `: ${notes}` : ""}`;
            try {
              enqueueFeedbackEvent({ version: props.appVersion, query: activeQuery, reason: "thumbs_down", topHitIds, topHitTitles, topHitScores, answerPreview: props.result.answer, feedbackText, bb_tags: ["kb_gap", ...(props.kbTestingModeEnabled ? ["kb_testing_mode"] : []), `thumbs_down_reason:${props.reason}`, ...(props.reason === "slang_uncommon_phrasing" ? ["slang_phrase"] : [])], threadId: props.askThreadId || undefined, threadTurn: props.askThreadTurnsCount || undefined, queryKind: props.nextAskKind, locationId: props.locationId, locationName: props.locationName, salespersonFirstName: props.salespersonFirstName });
              if (props.askThreadId && props.canEscalateKbThread) {
                try {
                  await addTrainingThreadFeedback({ organizationId: props.scopedOrganizationId, threadId: props.askThreadId, feedbackText, feedbackKind: props.reason === "slang_uncommon_phrasing" ? "coverage_gap" : "thumbs_down", guidancePurpose: props.activeGuidance?.purpose, guidanceProduct: props.activeGuidance?.product, lane: props.activeGuidance?.lane as KbLane | undefined, confidenceBand: props.result.hits[0]?.score != null ? (props.result.hits[0].score >= 0.72 ? "high" : props.result.hits[0].score >= 0.6 ? "medium" : "low") : undefined });
                } catch (threadErr) {
                  if (!isThreadNotFoundError(threadErr)) throw threadErr;
                }
              }
              await adminPost<{ ok: true; saved: number }>("/api/kb/admin/saveGaps", { submittedBy: props.salespersonName || props.salespersonFirstName || "training-user", sourceArticleSlug: props.result.hits[0]?.id || undefined, sourceTopHits: topHits.map((h) => ({ id: h.id, title: h.title, score: h.score })), items: [{ item_type: "gap", title: `${props.kbTestingModeEnabled ? "[KB testing] " : ""}Training thumbs-down: ${activeQuery.slice(0, 120)}`, summary: props.kbTestingModeEnabled ? [`Reason: ${reasonLabel}.`, `Exact question phrasing: ${activeQuery}`, `Expected answer: ${expectedAnswer || "(missing)"}`, `What was wrong: ${notes || "(missing)"}`, `Impact: ${impact || "(missing)"}`].join(" ") : `${reasonLabel}. ${notes || "No extra notes provided."}`, priority: props.reason === "wrong_answer" || props.reason === "outdated_policy" ? "high" : "medium", article_draft: props.reason === "slang_uncommon_phrasing" ? `## Slang phrasing to support\n\n- User phrasing: "${activeQuery}"\n- Expected meaning: ${notes || expectedAnswer || "(needs trainer clarification)"}\n${props.kbTestingModeEnabled ? `- Impact: ${impact || "(missing impact note)"}\n` : ""}\n## Proposed KB updates\n\n- Add alternate phrasing examples and synonyms that map this wording to the correct policy/article.\n` : "" }], organizationId: props.scopedOrganizationId || undefined });
              if (props.webhookUrl) {
                await submitTrainingFeedbackQueue({ webhookUrl: props.webhookUrl, appVersion: props.appVersion, locationId: props.locationId, locationName: props.locationName, salespersonFirstName: props.salespersonFirstName });
              }
              props.setMessage({ tone: "ok", text: "Saved. This was added to the KB gap inbox for trainer+ follow-up." });
              resetForm();
            } catch (err) {
              props.setMessage({ tone: "err", text: err instanceof Error ? err.message : "Failed to submit feedback." });
            } finally {
              props.setBusy(false);
            }
          }}
        >
          {props.busy ? "Submitting..." : "Submit feedback"}
        </button>
        <button type="button" className={`${secondaryBtnClass} px-2.5 py-1.5 text-xs font-semibold`} onClick={resetForm}>Cancel</button>
      </div>
    </div>
  );
}
